import React, { useState, useEffect, useMemo } from 'react';
import {
  FaBook, FaSearch, FaFileExcel,
  FaTimes, FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import * as XLSX from 'xlsx-js-style';
import api from '../../../utils/api';
import useColleges from '../../../utils/useColleges';
import { useAuth } from '../../../context/AuthContext';
import './ExpenseBook.css';

const ExpenseBook = () => {
  const { user } = useAuth();
  const { colleges, loading: collegesLoading } = useColleges();
  const isScoped = user?.role === 'collegeadmin' || user?.role === 'clerk';

  // Data States
  const [expenses, setExpenses] = useState([]);
  const [heads, setHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);

  // Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [filterHead, setFilterHead] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Initialization: Set default college if scoped
  useEffect(() => {
    if (isScoped && colleges.length > 0 && !filterCollege) {
      setFilterCollege(colleges[0]._id);
    }
  }, [isScoped, colleges, filterCollege]);

  // Fetch Data on mount
  useEffect(() => {
    fetchExpenses();
    fetchHeads();
  }, []);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/expenses', { params: { limit: 5000 } });
      setExpenses(data.data || []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHeads = async () => {
    try {
      const { data } = await api.get('/expenses/heads');
      setHeads(data.data || []);
    } catch (error) {
      console.error('Error fetching heads:', error);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    if (!isScoped) setFilterCollege('');
    setFilterHead('');
    setFromDate('');
    setToDate('');
  };

  // ── Apply Filters ──
  const filteredExpenses = useMemo(() => {
    return expenses.filter(exp => {
      const search = searchQuery.toLowerCase();
      const matchSearch =
        (exp.particular || '').toLowerCase().includes(search) ||
        (exp.referenceNo || '').toLowerCase().includes(search);

      const expCollegeId = exp.college?._id || exp.college;
      const matchCollege = filterCollege ? String(expCollegeId) === String(filterCollege) : true;

      const expHeadId = exp.head?._id || exp.head;
      const matchHead = filterHead ? String(expHeadId) === String(filterHead) : true;

      let matchDate = true;
      if (fromDate || toDate) {
        if (!exp.expenseDate) {
          matchDate = false;
        } else {
          const expDate = new Date(exp.expenseDate);
          expDate.setHours(0, 0, 0, 0);
          
          if (fromDate) {
            const fDate = new Date(fromDate);
            fDate.setHours(0, 0, 0, 0);
            if (expDate < fDate) matchDate = false;
          }
          if (toDate) {
            const tDate = new Date(toDate);
            tDate.setHours(0, 0, 0, 0);
            if (expDate > tDate) matchDate = false;
          }
        }
      }

      return matchSearch && matchCollege && matchHead && matchDate;
    });
  }, [expenses, searchQuery, filterCollege, filterHead, fromDate, toDate]);

  // ── Colleges to Display (For Grouped Tables) ──
  const collegesToDisplay = useMemo(() => {
    if (filterCollege) return colleges.filter(c => c._id === filterCollege);
    return colleges.length > 0 ? colleges : [{ _id: 'default', name: 'Loading Colleges...' }];
  }, [colleges, filterCollege]);

  // ── Calculations ──
  const totalAmount = useMemo(() => {
    return filteredExpenses.reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0);
  }, [filteredExpenses]);

  // Date/Time Formatters
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';

  // ==========================================
  // PERFECT EXPORT TO EXCEL
  // ==========================================
  const handleExportExcel = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    const todayFormatted = `${dd}-${mm}-${yyyy}`;
    const headers = [
      'S.No', 'Head', 'Particulars', 'Amount (₹)',
      'Payment Mode', 'Debited Bank', 'Reference No', 'Expense Date', 'Created Date', 'Updated Date'
    ];

    const activeCollege = colleges.find(c => c._id === filterCollege)?.name || 'All Colleges';
    const activeHead = heads.find(h => h._id === filterHead)?.name || 'All Heads';
    
    const formatDateDDMMYYYY = (dateStr) => {
      if (!dateStr) return 'Any';
      const [yyyy, mm, dd] = dateStr.split('-');
      return `${dd}-${mm}-${yyyy}`;
    };

    const filtersText = `Filters Applied -> College: ${activeCollege} | Head: ${activeHead} | From: ${formatDateDDMMYYYY(fromDate)} | To: ${formatDateDDMMYYYY(toDate)} | Search: ${searchQuery || 'None'}`;

    const colsCount = headers.length;
    const aoa = [
      ['EXPENSE BOOK LEDGER', ...Array(colsCount - 1).fill('')],
      [filtersText, ...Array(colsCount - 1).fill('')],
      Array(colsCount).fill('')
    ];

    let merges = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colsCount - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: colsCount - 1 } }
    ];

    let currentRow = 3; // 0-indexed

    collegesToDisplay.forEach((college) => {
      const collegeExpenses = filteredExpenses.filter(exp => (exp.college?._id || exp.college) === college._id);
      if (collegeExpenses.length === 0 && !filterCollege) return;

      const collTotal = collegeExpenses.reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0);

      // 1. College Header Row (Centered and Bold) - Includes College Total
      aoa.push([`${college.name.toUpperCase()} - TOTAL: ₹ ${collTotal.toLocaleString()}`, ...Array(colsCount - 1).fill('')]);
      merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: colsCount - 1 } });
      currentRow++;

      // 2. Column Headers
      aoa.push(headers);
      currentRow++;

      // 3. Data Rows
      collegeExpenses.forEach((exp, index) => {
        const amt = Number(exp.amount || 0);
        const bankDisplay = exp.paymentMode === 'Cash'
          ? '-'
          : (exp.bankName && exp.bankAccount
              ? `${exp.bankName} - ${exp.bankAccount}`
              : (exp.bank?.name && exp.bank?.accountNumber
                  ? `${exp.bank.name} - ${exp.bank.accountNumber}`
                  : '-'));
        aoa.push([
          index + 1,
          exp.head?.name || '-',
          exp.particular || '-',
          amt,
          exp.paymentMode || '-',
          bankDisplay,
          exp.referenceNo || '-',
          fmtDate(exp.expenseDate),
          fmtDateTime(exp.createdAt),
          fmtDateTime(exp.updatedAt)
        ]);
        currentRow++;
      });

      // Add empty space between colleges
      aoa.push(Array(colsCount).fill(''));
      currentRow++;
    });

    // 5. Overall Grand Total (Only show if not filtering by a single college)
    if (!filterCollege) {
      aoa.push(['', '', 'GRAND TOTAL:', totalAmount, '', '', '', '', '', '']);
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = merges;

    // Properly sized columns
    ws['!cols'] = [
      { wch: 8 },  // S.No
      { wch: 25 }, // Head
      { wch: 35 }, // Particulars
      { wch: 15 }, // Amount
      { wch: 15 }, // Payment Mode
      { wch: 30 }, // Debited Bank
      { wch: 20 }, // Reference No
      { wch: 15 }, // Expense Date
      { wch: 22 }, // Created Date
      { wch: 22 }  // Updated Date
    ];

    const border = {
      top: { style: 'thin', color: { auto: 1 } }, bottom: { style: 'thin', color: { auto: 1 } },
      left: { style: 'thin', color: { auto: 1 } }, right: { style: 'thin', color: { auto: 1 } }
    };

    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const ref = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[ref]) ws[ref] = { v: '', t: 's' };

        if (R === 0) { // Title
          ws[ref].s = { font: { name: 'Arial', sz: 16, bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1E3A5F' } }, alignment: { vertical: 'center', horizontal: 'center' } };
        } else if (R === 1) { // Filters
          ws[ref].s = { font: { name: 'Arial', sz: 10, italic: true, color: { rgb: '2C3E50' } }, fill: { fgColor: { rgb: 'F1F5F9' } }, alignment: { vertical: 'center', horizontal: 'center' }, border };
        } else if (ws[ref].v && typeof ws[ref].v === 'string' && ws[ref].v.includes('GRAND TOTAL:')) {
          // Total Rows
          ws[ref].s = { font: { name: 'Arial', sz: 11, bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: 'D97706' } }, alignment: { vertical: 'center', horizontal: 'right' }, border };
        } else if (ws[ref].v && typeof ws[ref].v === 'string' && ws[ref].v.includes(' - TOTAL: ₹ ') && C === 0 && R > 2) {
          // College Name Header Row (with Total)
          ws[ref].s = { font: { name: 'Arial', sz: 12, bold: true, color: { rgb: '1E3A5F' } }, fill: { fgColor: { rgb: 'E2E8F0' } }, alignment: { vertical: 'center', horizontal: 'center' }, border };
        } else if (headers.includes(ws[ref].v)) { // Table Headers
          ws[ref].s = { font: { name: 'Arial', sz: 10, bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '34495E' } }, alignment: { vertical: 'center', horizontal: 'center' }, border };
        } else if (R > 2 && ws[ref].v !== '') { // Data Cells
          ws[ref].s = { font: { name: 'Arial', sz: 9 }, alignment: { vertical: 'center', horizontal: 'center', wrapText: true }, border };
          if (C === 2) ws[ref].s.alignment.horizontal = 'left'; // Align particulars to left
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ExpenseBook');
    XLSX.writeFile(wb, `Expense_Book_Ledger_${todayFormatted}.xlsx`);
  };

  return (
    <div className="expense-book-container">
      {/* ── Header ── */}
      <div className="expense-book-header-row">
        <h2 className="expense-book-title">
          <FaBook /> Expense Book Ledger
        </h2>
        <div className="expense-book-export-btns">
          <button className="expense-book-btn-excel" onClick={handleExportExcel} disabled={filteredExpenses.length === 0}>
            <FaFileExcel /> Export to Excel
          </button>
        </div>
      </div>

      {/* ── Summary Strip ── */}
      {filteredExpenses.length > 0 && (
        <div className="expense-book-summary-strip">
          <span>Showing <strong>{filteredExpenses.length}</strong> expenses</span>
          {/* Hide Grand Total if specific college is selected */}
          {!filterCollege && (
            <span className="expense-book-summary-amount">Grand Total: <strong>₹ {totalAmount.toLocaleString()}</strong></span>
          )}
        </div>
      )}

      {/* ── Controls (Filters) ── */}
      <div className="expense-book-controls">
        <div className="expense-book-search-wrapper">
          <FaSearch className="expense-book-search-icon" />
          <input
            type="text"
            className="expense-book-search-input"
            placeholder="Search particulars, ref no..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {!isScoped && (
          <select className="expense-book-select" value={filterCollege} onChange={e => setFilterCollege(e.target.value)}>
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        <select className="expense-book-select" value={filterHead} onChange={e => setFilterHead(e.target.value)}>
          <option value="">All Heads</option>
          {heads.map(h => <option key={h._id} value={h._id}>{h.name}</option>)}
        </select>

        <input
          type="date"
          className="expense-book-select"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          title="From Date"
        />

        <input
          type="date"
          className="expense-book-select"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          min={fromDate}
          title="To Date"
        />

        {(filterCollege && !isScoped) || filterHead || fromDate || toDate || searchQuery ? (
          <button className="expense-book-btn-clear" onClick={handleClearFilters}>
            <FaTimes /> Clear
          </button>
        ) : null}
      </div>

      {/* ── Grouped Tables by College ── */}
      <div className="expense-book-grouped-container">
        {loading ? (
          <div className="expense-book-state-msg">Loading records...</div>
        ) : filteredExpenses.length === 0 ? (
          <div className="expense-book-state-msg expense-book-empty">No expenses found for the selected filters.</div>
        ) : (
          collegesToDisplay.map(college => {
            const collegeExpenses = filteredExpenses.filter(exp => (exp.college?._id || exp.college) === college._id);

            if (collegeExpenses.length === 0 && !filterCollege) return null;

            const collegeTotal = collegeExpenses.reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0);

            return (
              <div key={college._id} className="expense-book-table-group">
                <h3 className="expense-book-group-header">
                  <span className="expense-book-college-name">🏫 {college.name}</span>
                  <div className="expense-book-group-stats">
                    <span className="expense-book-group-count">{collegeExpenses.length} expenses</span>
                    <span className="expense-book-group-total">Total: ₹ {collegeTotal.toLocaleString()}</span>
                  </div>
                </h3>
                <div className="expense-book-table-wrapper">
                  <table className="expense-book-table">
                    <thead>
                      <tr>
                        <th width="50px">S.No</th>
                        <th className="expense-book-col-head expense-book-align-left">Head</th>
                        <th className="expense-book-align-left">Particulars</th>
                        <th className="expense-book-col-amount">Amount</th>
                        <th className="expense-book-col-paymode">Payment Mode</th>
                        <th className="expense-book-col-bank">Debited Bank</th>
                        <th className="expense-book-col-ref">Ref No</th>
                        <th className="expense-book-col-date">Date</th>
                        <th className="expense-book-col-cdate">Created Date</th>
                        <th className="expense-book-col-udate">Updated Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collegeExpenses.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="expense-book-no-results">
                            <span className="expense-book-no-results-icon">🔍</span>
                            No matching expenses found for this college
                          </td>
                        </tr>
                      ) : (
                        collegeExpenses.map((exp, index) => (
                          <React.Fragment key={exp._id}>
                            <tr className="expense-book-main-row">
                              <td>{index + 1}</td>
                              <td className="expense-book-col-head expense-book-align-left" style={{ fontWeight: 600, color: '#475569' }}>
                                {exp.head?.name || '—'}
                              </td>
                              <td className="expense-book-align-left">
                                <div className="expense-book-name-wrapper" onClick={() => setExpandedRow(expandedRow === exp._id ? null : exp._id)}>
                                  <div style={{ fontWeight: 700, color: '#0f172a' }}>{exp.particular || '—'}</div>
                                  <button className="expense-book-expand-btn">
                                    {expandedRow === exp._id ? <FaChevronUp /> : <FaChevronDown />}
                                  </button>
                                </div>
                              </td>
                              <td className="expense-book-col-amount expense-book-amt-total">
                                ₹ {Number(exp.amount || 0).toLocaleString()}
                              </td>
                              <td className="expense-book-col-paymode">
                                <span className="expense-book-badge">{exp.paymentMode || 'Cash'}</span>
                              </td>
                              <td className="expense-book-col-bank" style={{ color: '#334155', fontSize: '0.85rem', fontWeight: 500 }}>
                                {exp.paymentMode === 'Cash'
                                  ? '—'
                                  : (exp.bankName && exp.bankAccount
                                      ? `${exp.bankName} - ${exp.bankAccount}`
                                      : (exp.bank?.name && exp.bank?.accountNumber
                                          ? `${exp.bank.name} - ${exp.bank.accountNumber}`
                                          : '—'))
                                }
                              </td>
                              <td className="expense-book-col-ref" style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                {exp.referenceNo || '—'}
                              </td>
                              <td className="expense-book-col-date" style={{ fontWeight: 600, color: '#334155' }}>
                                {fmtDate(exp.expenseDate)}
                              </td>
                              <td className="expense-book-col-cdate" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                {fmtDateTime(exp.createdAt)}
                              </td>
                              <td className="expense-book-col-udate" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                {fmtDateTime(exp.updatedAt)}
                              </td>
                            </tr>

                            {/* Expanded Row for Responsive View */}
                            {expandedRow === exp._id && (
                              <tr className="expense-book-expanded-row">
                                <td colSpan="9" style={{ padding: 0 }}>
                                  <div className="expense-book-expanded-content">
                                    <div className="expense-book-expanded-grid">
                                      <div className="expense-book-exp-item expense-book-exp-head">
                                        <span className="expense-book-exp-label">Head</span>
                                        <span className="expense-book-exp-value">{exp.head?.name || '—'}</span>
                                      </div>
                                      <div className="expense-book-exp-item expense-book-exp-amount">
                                        <span className="expense-book-exp-label">Amount</span>
                                        <span className="expense-book-exp-value expense-book-amt-total">₹ {Number(exp.amount || 0).toLocaleString()}</span>
                                      </div>
                                      <div className="expense-book-exp-item expense-book-exp-paymode">
                                        <span className="expense-book-exp-label">Payment Mode</span>
                                        <span className="expense-book-exp-value"><span className="expense-book-badge">{exp.paymentMode || 'Cash'}</span></span>
                                      </div>
                                      <div className="expense-book-exp-item expense-book-exp-bank">
                                        <span className="expense-book-exp-label">Debited Bank</span>
                                        <span className="expense-book-exp-value">
                                          {exp.paymentMode === 'Cash'
                                            ? '—'
                                            : (exp.bankName && exp.bankAccount
                                                ? `${exp.bankName} - ${exp.bankAccount}`
                                                : (exp.bank?.name && exp.bank?.accountNumber
                                                    ? `${exp.bank.name} - ${exp.bank.accountNumber}`
                                                    : '—'))
                                          }
                                        </span>
                                      </div>
                                      <div className="expense-book-exp-item expense-book-exp-ref">
                                        <span className="expense-book-exp-label">Ref No</span>
                                        <span className="expense-book-exp-value">{exp.referenceNo || '—'}</span>
                                      </div>
                                      <div className="expense-book-exp-item expense-book-exp-date">
                                        <span className="expense-book-exp-label">Date</span>
                                        <span className="expense-book-exp-value">{fmtDate(exp.expenseDate)}</span>
                                      </div>
                                      <div className="expense-book-exp-item expense-book-exp-cdate">
                                        <span className="expense-book-exp-label">Created Date</span>
                                        <span className="expense-book-exp-value">{fmtDateTime(exp.createdAt)}</span>
                                      </div>
                                      <div className="expense-book-exp-item expense-book-exp-udate">
                                        <span className="expense-book-exp-label">Updated Date</span>
                                        <span className="expense-book-exp-value">{fmtDateTime(exp.updatedAt)}</span>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ExpenseBook;